import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import MainPage from './pages/MainPage';
import HomePage from './pages/HomePage';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminStatsPage from './pages/AdminStatsPage';
import AdminUsagePage from './pages/AdminUsagePage';
import BillingPage from './pages/BillingPage';
import CampaignsPage from './pages/CampaignsPage';
import TemplatesPage from './pages/TemplatesPage';
import BotFlowPage from './pages/BotFlowPage';
import SettingsPage from './pages/SettingsPage';
import KnowledgePage from './pages/KnowledgePage';
import AISettingsPage from './pages/AISettingsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import { ChatProvider } from './context/ChatContext';
import { NotificationProvider } from './context/NotificationContext';
import SupportPage from './pages/SupportPage';
import DashboardContainer from './components/layout/DashboardContainer';
import ProtectedRoute from './components/layout/ProtectedRoute';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import DataDeletionPage from './pages/legal/DataDeletionPage';

/**
 * This component wraps the application and applies the correct layout
 * based on the current URL.
 */
function ChromeAwareLayout({ children }) {
  const location = useLocation();
  const isAuthPage = location.pathname.startsWith('/login') || location.pathname.startsWith('/signup');
  const isDashboard = ['/main', '/templates', '/campaigns', '/billing', '/help', '/bot', '/knowledge', '/ai-settings', '/analytics', '/admin'].some(p => location.pathname.startsWith(p));

  let layoutClass = 'default-layout';
  if (isDashboard) {
    layoutClass = 'main-page-layout';
  } else if (isAuthPage) {
    layoutClass = 'login-page-layout';
  }

  return (
    <div className={layoutClass}>
      {children}
    </div>
  );
}

function App() {
  return (
    <Router>
      <NotificationProvider>
        <ChatProvider>
          <ChromeAwareLayout>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/data-deletion" element={<DataDeletionPage />} />

              {/* Protected client routes */}
              <Route path="/main" element={<ProtectedRoute><MainPage /></ProtectedRoute>} />
              <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
              <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
              <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
              <Route path="/bot" element={<ProtectedRoute><BotFlowPage /></ProtectedRoute>} />
              <Route path="/knowledge" element={<ProtectedRoute><KnowledgePage /></ProtectedRoute>} />
              <Route path="/ai-settings" element={<ProtectedRoute><AISettingsPage /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
              <Route path="/help" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

              {/* Protected admin routes */}
              <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboardPage /></ProtectedRoute>} />
              <Route path="/admin/stats" element={<ProtectedRoute role="admin"><AdminStatsPage /></ProtectedRoute>} />
              <Route path="/admin/usage" element={<ProtectedRoute role="admin"><AdminUsagePage /></ProtectedRoute>} />

              {/* Default: go to signup for new users */}
              <Route path="*" element={<HomePage />} />
            </Routes>
          </ChromeAwareLayout>
        </ChatProvider>
      </NotificationProvider>
    </Router>
  );
}

export default App;
